import { documentReady } from "@sazka/utils/document-ready";
import { CustomEaseDurations, CustomEaseNames, useGsap } from "@sazka/utils/gsap-register";
import { ref, watch } from "vue";

const initSegmentedPicker = (picker: HTMLElement) => {
    const items = [...picker.querySelectorAll<HTMLButtonElement>(".ds-segmented-picker__toggle")];
    const pickerSlider = picker.querySelector<HTMLElement>(".ds-segmented-picker__slider");

    if (!pickerSlider || items.length === 0) return;
    const { gsap } = useGsap();

    let buttonWidthsCache: number[] = [];

    const moveSlider = (index: number, withoutAnimation = false) => {
        const currentButton = items[index];
        if (!currentButton) return;

        const currentWidth = buttonWidthsCache[index];
        pickerSlider.style.width = `${currentWidth}px`;

        const totalWidth = buttonWidthsCache.slice(0, index).reduce((acc, width) => acc + width, 0);

        let duration = CustomEaseDurations.MOTION_DURATION_250;
        if (withoutAnimation) {
            duration = 0;
        }

        gsap.to(pickerSlider, {
            translateX: totalWidth,
            duration: duration,
            ease: CustomEaseNames.MOTION_EASE_OUT,
        });
    };

    const currentActiveIndex = ref(0);
    watch(currentActiveIndex, (newCurrentIndex) => {
        moveSlider(newCurrentIndex);
    });

    const calculateWidths = (entries: ResizeObserverEntry[]) => {
        buttonWidthsCache = entries.map((entry) => Math.round(entry.borderBoxSize[0].inlineSize));
    };

    items.forEach((item, index) => {
        item.addEventListener("click", () => {
            const currentActiveButton = items[currentActiveIndex.value];
            currentActiveButton.classList.remove("is-active");

            const inactiveItemElementId = currentActiveButton.dataset.item;
            const inactiveItemElement = document.getElementById(inactiveItemElementId);
            if (inactiveItemElement) {
                inactiveItemElement.classList.add("d-none");
            }

            const activeItemElementId = item.dataset.item;
            const activeItemElement = document.getElementById(activeItemElementId);
            if (activeItemElement) {
                activeItemElement.classList.remove("d-none");
            }

            currentActiveIndex.value = index;
            item.classList.add("is-active");
        });
    });

    const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        // Wait for all items to be rendered before measuring
        // Without it, it would cause re-trigger of the observer, cause of font loading
        if (entries.length < items.length) return;

        calculateWidths(entries);
        moveSlider(currentActiveIndex.value, true);
    });

    items.forEach((item) => resizeObserver.observe(item));

    window.addEventListener("beforeunload", () => {
        resizeObserver.disconnect();
    });
};

documentReady(() => {
    const pickers = document.querySelectorAll<HTMLElement>(".ds-segmented-picker.ds-segmented-picker--need-init");
    pickers.forEach((p) => initSegmentedPicker(p));
});
